import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { hrAPI } from "../../services/api";
import { Search, Eye, Trash2, Filter } from "lucide-react";

interface Props {
  onSelect: (id: number) => void;
  initialSearch?: string;
  initialDept?: string;
}

const fmt = (n: number) => new Intl.NumberFormat("en-US").format(n || 0);

export default function EmployeeList({ onSelect, initialSearch="", initialDept="" }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState(initialSearch);
  const [status, setStatus]       = useState("");
  const [dept, setDept]           = useState(initialDept);
  const [toast, setToast]         = useState("");

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await hrAPI.employeesList({ search, status, department: dept });
      if (r.success) setEmployees(r.employees);
    } catch { showToast("❌ خطا در اتصال به سرور"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [search, status, dept]);
  useEffect(() => { setSearch(initialSearch); setDept(initialDept); }, [initialSearch, initialDept]);

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`آیا مطمئن هستید که "${name}" حذف شود؟`)) return;
    try {
      const r = await hrAPI.employeeDelete(id);
      if (r.success) { showToast("✅ کارمند حذف شد"); load(); }
      else showToast("❌ " + r.message);
    } catch { showToast("❌ خطا در اتصال به سرور"); }
  };

  const inp: React.CSSProperties = {
    padding: "9px 14px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 10, color: T.text, fontSize: 13,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl" as const, outline: "none",
  };

  return (
    <div dir="rtl">
      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 999, padding: "12px 24px", borderRadius: 12, background: dark ? "#1e293b" : "#fff", border: `1px solid`, boxShadow: "0 8px 32px rgba(0,0,0,0.3)", color: T.text, fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>
          {toast}
        </div>
      )}

      {/* فیلترها */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <Search size={14} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: T.subtext }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="جستجو در نام، کد، شماره..." style={{ ...inp, width: "100%", paddingRight: 36, boxSizing: "border-box" as const }} />
        </div>
        <div style={{ position: "relative", minWidth: 160 }}>
          <input value={dept} onChange={e => setDept(e.target.value)} placeholder="فیلتر بخش..." style={{ ...inp, width: "100%", boxSizing: "border-box" as const }} />
          {dept && <button onClick={() => setDept("")} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 16 }}>×</button>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Filter size={14} color={T.subtext} />
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inp, background: dark ? "#1e293b" : "#f8faff" }}>
            <option value="">همه وضعیت‌ها</option>
            <option value="active">فعال</option>
            <option value="inactive">غیرفعال</option>
            <option value="terminated">اخراج</option>
          </select>
        </div>
      </div>

      {dept && (
        <div style={{ marginBottom: 14, padding: "8px 14px", borderRadius: 10, background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.2)", fontSize: 13, color: "#8b5cf6", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>👔 بخش: {dept}</span>
          <button onClick={() => setDept("")} style={{ border: "none", background: "transparent", color: "#8b5cf6", cursor: "pointer", fontSize: 16, fontWeight: 900 }}>×</button>
        </div>
      )}

      {/* جدول */}
      <div style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${T.cardBorder}`, background: dark ? "rgba(255,255,255,0.03)" : "#fff" }}>
        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 130px 130px 130px 140px 80px", gap: 12, padding: "12px 18px", background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", borderBottom: `1px solid ${T.cardBorder}`, fontSize: 11, fontWeight: 700, color: T.subtext }}>
          <span>کد</span><span>نام کارمند</span><span>وظیفه</span><span>بخش</span><span>شماره تماس</span><span>معاش</span><span>عملیات</span>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 50, color: T.subtext }}>
            <div style={{ width: 32, height: 32, border: "3px solid rgba(139,92,246,0.2)", borderTop: "3px solid #8b5cf6", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
            در حال بارگذاری...
          </div>
        ) : employees.length === 0 ? (
          <div style={{ textAlign: "center", padding: 50, color: T.subtext }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>👥</div>
            <div>هیچ کارمندی پیدا نشد</div>
          </div>
        ) : employees.map((e, i) => (
          <div key={e.id}
            style={{ display: "grid", gridTemplateColumns: "80px 1fr 130px 130px 130px 140px 80px", gap: 12, padding: "14px 18px", borderBottom: i < employees.length - 1 ? `1px solid ${T.cardBorder}` : "none", alignItems: "center", cursor: "pointer", transition: "background 0.15s" }}
            onMouseEnter={el => (el.currentTarget.style.background = dark ? "rgba(255,255,255,0.03)" : "rgba(139,92,246,0.03)")}
            onMouseLeave={el => (el.currentTarget.style.background = "")}
            onClick={() => onSelect(e.id)}
          >
            <span style={{ fontSize: 11, fontFamily: "monospace", color: "#8b5cf6", fontWeight: 700 }}>{e.code}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "white", fontWeight: 700, overflow: "hidden", flexShrink: 0 }}>
                {e.photo_url
                  ? <img src={e.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={el => { (el.target as HTMLImageElement).style.display = "none"; }} />
                  : `${e.first_name?.[0]}${e.last_name?.[0]}`
                }
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{e.first_name} {e.last_name}</div>
                <div style={{ fontSize: 11, color: T.subtext }}>پدر: {e.father_name || "—"}</div>
              </div>
            </div>
            <span style={{ fontSize: 12, color: T.text }}>{e.position || "—"}</span>
            <span style={{ fontSize: 12, color: T.subtext }}>{e.department || "—"}</span>
            <span style={{ fontSize: 12, color: T.text, direction: "ltr" as const }}>{e.phone || "—"}</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#8b5cf6" }}>{fmt(e.current_salary)} {e.salary_currency || ""}</div>
              {e.total_loan > 0 && <div style={{ fontSize: 10, color: "#ef4444", marginTop: 2 }}>قرضه: {fmt(e.total_loan)}</div>}
            </div>
            <div style={{ display: "flex", gap: 6 }} onClick={ev => ev.stopPropagation()}>
              <button onClick={() => onSelect(e.id)} style={{ width: 30, height: 30, borderRadius: 8, border: "none", cursor: "pointer", background: "rgba(139,92,246,0.12)", color: "#8b5cf6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Eye size={13} />
              </button>
              <button onClick={() => handleDelete(e.id, `${e.first_name} ${e.last_name}`)} style={{ width: 30, height: 30, borderRadius: 8, border: "none", cursor: "pointer", background: "rgba(239,68,68,0.12)", color: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}